import { store } from "../store/store";
import {
  fetchWorkspaceConfig,
  liveConfigVersionChanged,
} from "../slices/workspaceConfigSlice";

// Push-based workspace-config freshness.
//
// The active workspace's config (`GET /workspaces/{id}/config`) is cached in
// the `workspaceConfig` slice, so a later config change (e.g. seeding a
// workspace's Execution View layout) would leave an open session on the stale
// config. Config writers bump a global `configVersion` token on
// `app_config/maintenance`, and each config response carries the token it was
// read at. This gate rides the maintenance-flag Firestore listener
// (useMaintenanceFlag): on every snapshot it compares the live token to the
// served one, and on a mismatch it refetches the active workspace's config in
// the background. Config-only changes ship no new JS, so a reload would be
// overkill here.

// The live token we last acted on. Keyed on the LIVE value (not a cooldown) so a
// refetch that's still in flight — or a served value that never converges —
// can't storm the backend; a later distinct token re-arms the refetch.
let lastHandledVersion = null;
let inFlight = false;

/**
 * Record the live global config token, then refetch the active workspace's
 * config if it was read at an older one. Recording the token is what makes
 * every other cached workspace refetch on its next selection, through the
 * slice's revision-aware fetch condition. No-op unless there's a real signal:
 *   - live token absent / non-string (field unset) → nothing to compare against;
 *   - not authenticated, no active workspace, or its config not yet loaded;
 *   - served token already equals the live one (session is current);
 *   - a refetch for this exact live token is already in flight / was just fired.
 */
export function maybeRefetchForStaleConfig(liveConfigVersion) {
  if (typeof liveConfigVersion !== "string" || !liveConfigVersion) return;
  if (store.getState().workspaceConfig?.liveConfigVersion !== liveConfigVersion) {
    store.dispatch(liveConfigVersionChanged(liveConfigVersion));
  }
  if (inFlight || liveConfigVersion === lastHandledVersion) return;

  const { auth, workspaceConfig } = store.getState();
  if (!auth?.isAuthenticated || !auth.id_client) return;
  const served = workspaceConfig?.byId?.[auth.id_client]?.config;
  if (!served) return;
  if (served.config_version === liveConfigVersion) return;

  lastHandledVersion = liveConfigVersion;
  inFlight = true;
  // Failures (offline / token) are owned by the 401 path; the slice keeps the
  // previous config on a failed refetch.
  Promise.resolve(
    store.dispatch(fetchWorkspaceConfig({ idClient: auth.id_client })),
  ).finally(() => {
    inFlight = false;
  });
}

/**
 * Test-only: reset the module guards between cases.
 * @internal
 */
export function _resetForTests() {
  lastHandledVersion = null;
  inFlight = false;
}
